import { DestroyRef, Injectable, inject } from '@angular/core';
import { Observable, of, retry, shareReplay, timer } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BigScreenEvent, BigScreenEventResponse, ClusterService, isEqual, isTruthy, WatchdogService } from '@core';
import { KVRepository } from '@core/lib/repositories';
import { HttpClient } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class BigScreenSchedule {
  private readonly destroyRef = inject(DestroyRef);
  private readonly cluster = inject(ClusterService);
  private readonly kvRepository = inject(KVRepository);
  private readonly watchdog = inject(WatchdogService);
  private readonly http = inject(HttpClient);


  private readonly storageKey = 'bigScreenSchedule';
  private readonly logger = this.watchdog.tag('Big Screen Schedule', 'blue');

  public readonly schedule$ = this.kvRepository.one$(this.storageKey).pipe(
    map((data) => {
      if (data?.value) {
        return data.value as BigScreenEvent[];
      }

      return [] as BigScreenEvent[];
    }),
    shareReplay(1),
  );

  public initialize(): void {
    this.logger.info('Initialize');

    this.cluster.leader$.pipe(
      filter(isTruthy),
      takeUntilDestroyed(this.destroyRef),
      switchMap(() => this.getSchedule())
    ).subscribe((schedule) => {
        this.update(schedule);
    });
  }

  private getSchedule(): Observable<BigScreenEvent[]> {
    const url = `https://static.r2.oongalee.cloud/widgets/real-sports/events.json`;
    return this.http.get<BigScreenEventResponse>(url).pipe(
      map((response) => response.events as BigScreenEvent[] || []),
      retry({
        count: 3,
        delay: (_error, retryIndex) => timer(1000 * retryIndex)
      })
    );
  }

  public update(schedule: BigScreenEvent[]): void {
    this.schedule$.pipe(
      take(1),
      switchMap((scheduleLocal) => {
        if (isEqual(scheduleLocal, schedule)) {
          return of(schedule);
        }

        return this.kvRepository.update$({
          key: this.storageKey,
          value: schedule,
        });
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();
  }
}
